import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import onsiteproLogo from '../../assets/logo2 2.png';
import { parseSiteRegisterDate, formatSiteRegisterDate, siteRegisterDatePickerProps } from './adminEditModalTheme';

const cell = {
  border: '1px solid #000',
  padding: '4px 6px',
  fontSize: 11,
  verticalAlign: 'top',
  fontFamily: 'Arial, Helvetica, sans-serif',
  background: '#fff'
};

const thCell = {
  ...cell,
  textAlign: 'center',
  fontWeight: 700,
  padding: '6px 4px'
};

const inp = {
  width: '100%',
  border: 'none',
  outline: 'none',
  fontSize: 11,
  fontFamily: 'inherit',
  background: 'transparent',
  padding: '2px 0',
  boxSizing: 'border-box'
};

const toolCardStyles = [
  { bg: '#28a745', border: '#1e7e34', labelColor: '#fff', legend: 'Green', legendColor: '#28a745', desc: 'does not pose a significant risk' },
  { bg: '#FFC107', border: '#D39E00', labelColor: '#333', legend: 'Amber', legendColor: '#D39E00', desc: "average risk, generally up to 2 hours' use OK" },
  { bg: '#dc3545', border: '#c82333', labelColor: '#fff', legend: 'Red', legendColor: '#dc3545', desc: 'high vibration, monitor exposure carefully' }
];

const havInfoDefaults = {
  whatIsIt: 'Hand-arm vibration is vibration transmitted into your hands & arms when you use hand-held powered work equipment. Continued levels of vibration can cause Hand-Arm Vibration Syndrome (HAVS) & Carpal Tunnel Syndrome.',
  whatIsHavs: 'HAVS affects the nerves, blood vessels, muscles and joints of the hand, wrist and arm. It can become severely disabling if ignored. It includes vibration white finger, which can cause severe pain in the affected fingers.',
  whatIsCarpalTunnel: 'Carpal tunnel syndrome is a nerve disorder which may involve pain, tingling, numbness & weakness in parts of the hand, & can be caused by, among other things, exposure to vibration.',
  whoIsAtRisk: 'Anyone who regularly uses power tools, either at work or at home is at risk. The risk increases with the amount of time you spend operating tools. As a rule of thumb, watch for any feelings of numbness or loss of sensation in the hand fingers, after around 5 minutes of tool use.',
  whatAreTheSymptoms: 'Tingling and numbness in the fingers (which can cause sleep disturbance).\nNot being able to feel things with your fingers.\nLoss of strength in your hands (you may be less able to pick up or hold heavy objects).\nIn the cold and wet, the tips of your fingers going white then red and being painful.',
  doINeedHealthSurveillance: 'If you regularly use power tools then you should have completed a confidential vibration assessment questionnaire which is then assessed by Occupational Health and where necessary you will be contacted for an appointment. If you have not completed this questionnaire please contact your Supervisor. Health surveillance for HAVS will only be carried out by qualified Occupational nurses, and is confidential.',
  controlMeasuresList:
    'MAINTENANCE - Keep tools well maintained\nEXERCISE hands during work periods\nALWAYS report any symptoms\nSMOKING - don’t smoke and never when using tools\nUSE the right tool for the right job\nREPORT any faulty, poorly maintained or defective tools\nENSURE you keep your hands warm as possible\nSHORT breaks are better than one long one',
  controlMeasuresNote:
    'Some equipment come with labels, It is important to remember that the points/maximum time stated for any given tool is the total time the tool is actually in operation.'
};

function HandArmVibrationEditModalContent({
  edit,
  readOnly = false,
  pdfCapturePart = null,
  sumBlockTotalPoints,
  updateHavLogField,
  updateWeekDate,
  addHavLogWeek,
  removeHavLogWeek,
  updateToolLabelField,
  setToolLabelUsed,
  setEditHandArmVibration
}) {
  const logData = Array.isArray(edit.logData) && edit.logData.length ? edit.logData : [{ weekDate: '', rows: [] }];
  const emptyLogRow = { date: '', task: '', makeModel: '', triggerTime: '', pointsPerMin: '', totalPoints: '' };

  const addRowInWeek = (weekIdx) => {
    setEditHandArmVibration((prev) => {
      const nextLogData = (prev.logData || []).map((w) => ({
        ...w,
        rows: Array.isArray(w.rows) ? [...w.rows] : []
      }));
      if (!nextLogData[weekIdx]) return prev;
      nextLogData[weekIdx].rows.push({ ...emptyLogRow });
      return { ...prev, logData: nextLogData };
    });
  };

  const removeRowInWeek = (weekIdx) => {
    setEditHandArmVibration((prev) => {
      const nextLogData = (prev.logData || []).map((w) => ({
        ...w,
        rows: Array.isArray(w.rows) ? [...w.rows] : []
      }));
      if (!nextLogData[weekIdx] || nextLogData[weekIdx].rows.length <= 1) return prev;
      nextLogData[weekIdx].rows.pop();
      return { ...prev, logData: nextLogData };
    });
  };

  const signatureSrc = edit?.signature
    ? (String(edit.signature).startsWith('data:image') ? String(edit.signature) : `data:image/png;base64,${String(edit.signature)}`)
    : '';

  const showMainBlock = !pdfCapturePart || pdfCapturePart === 'aboveToolLabels';
  const showToolLabelsBlock = !pdfCapturePart || pdfCapturePart === 'toolLabelsOnly';

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', background: '#fff', border: '1px solid #000', padding: 12 }}>
      {showMainBlock && (
      <>
      {pdfCapturePart !== 'aboveToolLabels' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #000' }}>
        <img src={onsiteproLogo} alt="" style={{ height: 36, objectFit: 'contain' }} />
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3 }}>
          TAYLOR TECHNOLOGY SYSTEMS LIMITED
        </div>
      </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: 0 }}>
        <tbody>
          <tr>
            <td colSpan={6} style={{ ...thCell, fontSize: 15, padding: 12 }}>
              Hand Arm Vibration Log
            </td>
          </tr>
          <tr>
            <td colSpan={3} style={cell}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Name of Operator</div>
              {readOnly ? (
                <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{edit.operatorName || ''}</div>
              ) : (
                <input
                  type="text"
                  value={edit.operatorName || ''}
                  onChange={(e) => setEditHandArmVibration((prev) => ({ ...prev, operatorName: e.target.value }))}
                  style={inp}
                />
              )}
            </td>
            <td colSpan={2} style={cell}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Supervisors signature</div>
              <div
                style={{
                  border: '1px solid #bbb',
                  borderRadius: 6,
                  background: '#f5f5f5',
                  padding: 6,
                  height: 70,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {signatureSrc ? (
                  <img
                    src={signatureSrc}
                    alt="Supervisor signature"
                    style={{ maxWidth: '100%', maxHeight: '56px', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }}
                  />
                ) : (
                  <span style={{ fontSize: 11, color: '#777' }}>No signature</span>
                )}
              </div>
            </td>
            <td style={cell} />
          </tr>
          <tr>
            <th style={thCell}>Week Commencing (dd/mm/yy)</th>
            <th style={thCell}>Task</th>
            <th style={thCell}>Make / Model</th>
            <th style={thCell}>Trigger Time (mins)</th>
            <th style={thCell}>Points per minute</th>
            <th style={thCell}>Total Points</th>
          </tr>
          {logData.map((week, weekIdx) => (
            <React.Fragment key={weekIdx}>
              {(week.rows || []).map((row, rowIdx) => (
                <tr key={`${weekIdx}-${rowIdx}`}>
                  <td style={cell}>
                    {rowIdx === 0 ? (
                      readOnly ? (
                        <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{row.date || ''}</div>
                      ) : (
                        <div className="site-register-datepicker-wrap">
                          <DatePicker
                            {...siteRegisterDatePickerProps}
                            selected={parseSiteRegisterDate(row.date)}
                            onChange={(d) => updateHavLogField(weekIdx, rowIdx, 'date', formatSiteRegisterDate(d))}
                          />
                        </div>
                      )
                    ) : null}
                  </td>
                  <td style={cell}>
                    {readOnly ? (
                      <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{row.task || ''}</div>
                    ) : (
                      <input
                        type="text"
                        value={row.task || ''}
                        onChange={(e) => updateHavLogField(weekIdx, rowIdx, 'task', e.target.value)}
                        style={inp}
                      />
                    )}
                  </td>
                  <td style={cell}>
                    {readOnly ? (
                      <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{row.makeModel || ''}</div>
                    ) : (
                      <input
                        type="text"
                        value={row.makeModel || ''}
                        onChange={(e) => updateHavLogField(weekIdx, rowIdx, 'makeModel', e.target.value)}
                        style={inp}
                      />
                    )}
                  </td>
                  <td style={cell}>
                    {readOnly ? (
                      <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{row.triggerTime || ''}</div>
                    ) : (
                      <input
                        type="text"
                        value={row.triggerTime || ''}
                        onChange={(e) => updateHavLogField(weekIdx, rowIdx, 'triggerTime', e.target.value)}
                        style={inp}
                      />
                    )}
                  </td>
                  <td style={cell}>
                    {readOnly ? (
                      <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{row.pointsPerMin || ''}</div>
                    ) : (
                      <input
                        type="text"
                        value={row.pointsPerMin || ''}
                        onChange={(e) => updateHavLogField(weekIdx, rowIdx, 'pointsPerMin', e.target.value)}
                        style={inp}
                      />
                    )}
                  </td>
                  <td style={cell}>
                    {/* Total Points is auto-calculated = Trigger Time × Points per minute */}
                    <div style={{ ...inp, minHeight: 18, whiteSpace: 'pre-wrap' }}>{row.totalPoints || ''}</div>
                  </td>
                </tr>
              ))}
              {(() => {
                const sum = sumBlockTotalPoints(week.rows);
                const num = parseFloat(String(sum).replace(/,/g, '')) || 0;
                const exceeded = num >= 400; // absolute maximum exposure
                const redStyle = exceeded ? { color: '#c00' } : {};
                return (
                  <tr>
                    {exceeded ? (
                      <>
                        <td colSpan={2} style={cell} />
                        <td style={{ ...cell, fontWeight: 700, color: '#c00' }}>Exceeded maximum points per day inform Supervisor</td>
                        <td style={cell} />
                      </>
                    ) : (
                      <td colSpan={4} style={cell} />
                    )}
                    <td style={{ ...cell, fontWeight: 700, textAlign: 'right', ...redStyle }}>Total</td>
                    <td style={{ ...cell, fontWeight: 700, textAlign: 'right', ...redStyle }}>{sum}</td>
                  </tr>
                );
              })()}
              <tr>
                <td colSpan={6} style={{ ...cell, height: 14 }} />
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addHavLogWeek}>
              Add week block
            </button>
            {logData.length > 1 && (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeHavLogWeek(logData.length - 1)}>
                Remove last block
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={() => {
                if (logData.length) removeRowInWeek(logData.length - 1);
              }}
              disabled={!logData.length || !Array.isArray(logData[logData.length - 1]?.rows) || logData[logData.length - 1].rows.length <= 1}
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: (!logData.length || !Array.isArray(logData[logData.length - 1]?.rows) || logData[logData.length - 1].rows.length <= 1) ? '#c2a8b0' : '#7c1233',
                color: '#fff',
                cursor: (!logData.length || !Array.isArray(logData[logData.length - 1]?.rows) || logData[logData.length - 1].rows.length <= 1) ? 'not-allowed' : 'pointer',
                padding: 0,
                lineHeight: 1
              }}
            >
              <i className="fa fa-minus" aria-hidden style={{ fontSize: 11 }} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (logData.length) addRowInWeek(logData.length - 1);
              }}
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#7c1233',
                color: '#fff',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1
              }}
            >
              <i className="fa fa-plus" aria-hidden style={{ fontSize: 11 }} />
            </button>
          </div>
        </div>
      )}
      {readOnly && <div style={{ marginTop: 8, marginBottom: 12 }} />}

      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: 20 }}>
        <tbody>
          <tr>
            <td style={{ ...cell, width: '22%', textAlign: 'center', color: '#c00', fontWeight: 700 }}>100 points</td>
            <td style={{ ...cell, color: '#c00', fontSize: 11 }}>
              Control measures should be considered when your daily limit reaches 100 points. Share the workload to decrease your exposure and inform your Manager/ Supervisor.
            </td>
          </tr>
          <tr>
            <td style={{ ...cell, textAlign: 'center', color: '#c00', fontWeight: 700 }}>400 points</td>
            <td style={{ ...cell, color: '#c00', fontSize: 11 }}>
              If your daily exposure reaches the absolute maximum exposure of 400 points, stop work immediately and contact your Manager/ Supervisor &amp; HSE Advisor.
            </td>
          </tr>
        </tbody>
      </table>

      <div
        style={{
          background: '#1e3a5f',
          color: '#fff',
          padding: '12px 16px',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: 15,
          marginBottom: 12,
          border: '1px solid #000'
        }}
      >
        Managing Hand Arm Vibration (HAV)
      </div>

      <div style={{ border: '1px solid #1e3a5f', marginBottom: 20 }}>
        {[
          { title: 'What is it?', text: edit.whatIsIt || havInfoDefaults.whatIsIt },
          { title: 'What is HAVS ?', text: edit.whatIsHavs || havInfoDefaults.whatIsHavs },
          { title: 'What is Carpal Tunnel Syndrome?', text: edit.whatIsCarpalTunnel || havInfoDefaults.whatIsCarpalTunnel },
          { title: 'Who is at Risk?', text: edit.whoIsAtRisk || havInfoDefaults.whoIsAtRisk },
          { title: 'What are the Symptoms?', text: edit.whatAreTheSymptoms || havInfoDefaults.whatAreTheSymptoms },
          { title: 'Do I need to attend Health Surveillance?', text: edit.doINeedHealthSurveillance || havInfoDefaults.doINeedHealthSurveillance }
        ].map((row, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              borderBottom: i < 5 ? '1px solid #cbd5e0' : 'none',
              minHeight: 48
            }}
          >
            <div style={{ background: '#1e3a5f', color: '#fff', padding: 10, fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center' }}>
              {row.title}
            </div>
            <div style={{ padding: 10, fontSize: 12, color: '#1a202c', lineHeight: 1.45, whiteSpace: 'pre-line' }}>{row.text}</div>
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', borderTop: '1px solid #cbd5e0' }}>
          <div style={{ background: '#1e3a5f', color: '#fff', padding: 10, fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'flex-start' }}>
            What are the Control Measures?
          </div>
          <div style={{ padding: 10, fontSize: 12, color: '#1a202c' }}>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {(edit.controlMeasuresList || havInfoDefaults.controlMeasuresList).split('\n').map((item, idx) => {
                    if (!item.trim()) return null;
                    const parts = item.split(' - ');
                    return (
                      <li key={idx} style={{ marginBottom: 4 }}>
                        {parts.length > 1 ? (
                          <>
                            <strong>{parts[0]}</strong> - {parts.slice(1).join(' - ')}
                          </>
                        ) : (
                          <strong>{item}</strong>
                        )}
                      </li>
                    );
                  })}
            </ul>
          </div>
        </div>
      </div>
      </>
      )}

      {showToolLabelsBlock && (
      <>
      <div style={{ border: '1px solid #1e3a5f', marginBottom: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
          <div style={{ background: '#1e3a5f', color: '#fff', padding: 10, fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'flex-start' }}>
            Tool Labels – What do they mean?
          </div>
          <div style={{ padding: 10, fontSize: 12, color: '#1a202c', lineHeight: 1.45 }}>
            {edit.controlMeasuresNote || havInfoDefaults.controlMeasuresNote}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 8, border: '1px solid #1e3a5f', borderTop: 'none', padding: '12px 10px 8px' }}>
        {(edit.toolLabelsData || toolCardStyles.map((_, i) => ({}))).slice(0, 3).map((tool, idx) => {
          const cfg = toolCardStyles[idx];
          const t = tool || {};
          return (
            <div
              key={idx}
              style={{
                flex: '1 1 280px',
                maxWidth: 340,
                background: cfg.bg,
                border: `2px solid ${cfg.border}`,
                borderRadius: 12,
                padding: 16,
                color: cfg.labelColor
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'block', fontWeight: 700, marginBottom: 4 }}>Asset ID No:</div>
                {readOnly ? (
                  <div style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12, background: 'rgba(255,255,255,0.15)', minHeight: 20, whiteSpace: 'pre-wrap' }}>
                    {t.assetId || ''}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={t.assetId || ''}
                    onChange={(e) => updateToolLabelField(idx, 'assetId', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12 }}
                  />
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'block', fontWeight: 700, marginBottom: 4 }}>Vibration Level</div>
                {readOnly ? (
                  <div style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12, background: 'rgba(255,255,255,0.15)', minHeight: 20, whiteSpace: 'pre-wrap' }}>
                    {t.vibrationLevel || ''}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={t.vibrationLevel || ''}
                    onChange={(e) => updateToolLabelField(idx, 'vibrationLevel', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12 }}
                  />
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'block', fontWeight: 700, marginBottom: 4 }}>Maximum Daily Use</div>
                {readOnly ? (
                  <div style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12, background: 'rgba(255,255,255,0.15)', minHeight: 20, whiteSpace: 'pre-wrap' }}>
                    {t.maxDailyUse || ''}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={t.maxDailyUse || ''}
                    onChange={(e) => updateToolLabelField(idx, 'maxDailyUse', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12 }}
                  />
                )}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'block', fontWeight: 700, marginBottom: 4 }}>Points / min for Use</div>
                {readOnly ? (
                  <div style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12, background: 'rgba(255,255,255,0.15)', minHeight: 20, whiteSpace: 'pre-wrap' }}>
                    {t.pointsPerMin || ''}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={t.pointsPerMin || ''}
                    onChange={(e) => updateToolLabelField(idx, 'pointsPerMin', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${cfg.border}`, fontSize: 12 }}
                  />
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <i className="fa fa-user" style={{ color: '#2196F3', fontSize: 22 }} aria-hidden />
                {readOnly ? (
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{t.isUsed === true ? 'Yes' : 'No'}</div>
                ) : (
                  <div style={{ display: 'flex', gap: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontWeight: 700, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`havUsed-${idx}`}
                        checked={t.isUsed === true}
                        onChange={() => setToolLabelUsed(idx, true)}
                        style={{ marginRight: 6, width: 16, height: 16, accentColor: '#8b1634' }}
                      />
                      Yes
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontWeight: 700, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`havUsed-${idx}`}
                        checked={t.isUsed !== true}
                        onChange={() => setToolLabelUsed(idx, false)}
                        style={{ marginRight: 6, width: 16, height: 16, accentColor: '#8b1634' }}
                      />
                      No
                    </label>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 14, marginBottom: 8, color: '#fff' }}>
                {t.assetLabel || ['MTI 512', 'MTI 510', 'MTI 511'][idx]}
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>{cfg.legend}</span>
                <div style={{ color: '#fff', fontSize: 11, marginTop: 4, lineHeight: 1.35 }}>{cfg.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}

export default HandArmVibrationEditModalContent;
